import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import type { ComponentProps, CSSProperties } from "react";

// Shared, nesting-aware overlay z-index lives in the design system so DS and UI overlays stack
// correctly against each other during migration; see design-system/src/lib/overlay-z-index.tsx.
import { OverlayLayerProvider, useOverlayZIndex } from "@tellescope/design-system";

import { cn } from "../../lib/utils";
import { DialogContentContext } from "./DialogContentContext";
import { DialogOverlay } from "./DialogOverlay";
import { DialogPortal } from "./DialogPortal";

export const DialogContent = ({
  className,
  children,
  showCloseButton = true,
  disableCloseOnClickOutside = false,
  divided = false,
  overlayVariant = "dark",
  overlayProps,
  onInteractOutside,
  style,
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  disableCloseOnClickOutside?: boolean;
  divided?: boolean;
  overlayVariant?: "light" | "dark";
  overlayProps?: ComponentProps<typeof DialogOverlay>;
  width?: CSSProperties["width"];
}) => {
  // Prevent dialog from closing when clicking on elements marked as interactive with dialog
  const handleInteractOutside: ComponentProps<typeof DialogPrimitive.Content>["onInteractOutside"] = (e) => {
    const originalEvent = (e as CustomEvent).detail?.originalEvent;
    const target = (originalEvent?.target ?? e.target) as HTMLElement;
    if (disableCloseOnClickOutside || target?.closest("[data-interactive-with-dialog]")) {
      e.preventDefault();
    }
    onInteractOutside?.(e);
  };

  const isLightOverlay = overlayVariant === "light";

  const { z, style: zStyle, className: zClassName } = useOverlayZIndex("modal");

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay
        {...overlayProps}
        className={cn(isLightOverlay ? "bg-white/80 backdrop-blur-sm" : undefined, overlayProps?.className)}
      />
      {/* TODO: when this migrates to @tellescope/design-system, add a "contained" variant that scopes
          the dialog to an ancestor element instead of the viewport. Today the overlay is `fixed inset-0`
          and the content is `fixed top-1/2 left-1/2`, so a dialog always covers the whole screen even
          when portaled into a scoped container. A contained variant needs: positioning switched to
          `absolute` (overlay + content) so it resolves against the nearest positioned ancestor, the
          portal skipped (or targeted at that ancestor), and the z-index kept local rather than at the
          global `modal` layer. Consumers that need this hand-roll the overlay instead — see
          contact-details/src/components/ContactDetailsViewBody/FlaggedNoteOverlay.tsx. */}
      {/* TODO: when this migrates to @tellescope/design-system, add a two-pane (list + preview)
          size/layout variant. The `sm:max-w-lg` default below is too narrow for previewing rich
          email HTML, which sets its own fixed widths (a 600px table is the norm), so consumers
          currently override `max-w` and hand-roll the split themselves — see
          apps/web/src/components/inbox/InboxTemplateSelectorDialogContents.tsx. */}
      <DialogPrimitive.Content
        data-slot="dialog-content"
        style={{
          ...style,
          ...zStyle,
        }}
        className={cn(
          "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] rounded-lg border duration-200 sm:max-w-lg overflow-hidden",
          zClassName,
          isLightOverlay ? "" : "shadow-lg",
          divided ? "gap-0" : "gap-4 p-6",
          className
        )}
        onInteractOutside={handleInteractOutside}
        {...props}
      >
        {/* Anything opened from inside this dialog (a popover, select, nested dialog) stacks above it. */}
        <OverlayLayerProvider base={z}>
          <DialogContentContext.Provider value={{ divided }}>{children}</DialogContentContext.Provider>
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
            >
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </OverlayLayerProvider>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
};
